// ./src/utils/api/SentMessageService.ts
//
// Read-only view of messages the account has SENT through the sender (invitations, RSVP
// confirmations/notifications, cancellations, …) with per-recipient delivery status. Backed by the
// eventsmagic-api sentMessageList/sentMessageGet passthrough (which forwards the user's JWT to the
// sender). No writes — this is reporting.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { MESSAGE_PURPOSE_LABELS, SentMessageChannelKind, SentMessageDeliveryState } from "./sentMessagePurposes";

// The labels, the filter values and `purposesLabelled` are PURE (no imports) so a guard can run them.
export * from "./sentMessagePurposes";

const LIST_FIELDS = `
  id
  contextPath
  purpose
  status
  subject
  fromName
  fromValue
  channelId
  channel { channelKey name }
  totalRecipients
  totalDelivered
  totalFailed
  createdAt
  updatedAt
`;

const LIST_QUERY = gql`
  query SentMessageList($filter: SentMessageFilter, $params: PaginationAndSorting) {
    sentMessageList(filter: $filter, params: $params) {
      code
      errors { name errors warnings }
      data { ${LIST_FIELDS} }
    }
  }
`;

const GET_QUERY = gql`
  query SentMessageGet($id: ID) {
    sentMessageGet(id: $id) {
      code
      errors { name errors warnings }
      data {
        ${LIST_FIELDS}
        recipients {
          id
          recipient
          recipientType
          event
          status
          serviceProviderMessageId
          senderNumber
          createdAt
          updatedAt
        }
      }
    }
  }
`;

const PREVIEW_QUERY = gql`
  query SentMessagePreview($id: ID) {
    sentMessagePreview(id: $id) {
      code
      errors { name errors warnings }
      data { subject html text }
    }
  }
`;

export const messagePurposeLabel = (purpose?: string | null): string =>
  (purpose && MESSAGE_PURPOSE_LABELS[purpose]) || (purpose ? purpose.replace(/_/g, " ") : "Message");

/** Sender delivery status codes → { label, color }. Shared by message + per-recipient status. */
export const MESSAGE_STATUS: Record<number, { label: string; color: string }> = {
  100: { label: "Active", color: "gray" },
  300: { label: "Pending", color: "gray" },
  400: { label: "Sent", color: "blue" },
  500: { label: "Deferred", color: "yellow" },
  600: { label: "Delivered", color: "green" },
  800: { label: "Opened", color: "teal" },
  1000: { label: "Dropped", color: "red" },
  1100: { label: "Blocked", color: "red" },
  1200: { label: "Bounced", color: "orange" },
  1300: { label: "Spam report", color: "orange" },
  2000: { label: "Canceled", color: "gray" },
  3000: { label: "Failed", color: "red" },
};

export const messageStatus = (status?: number | null): { label: string; color: string } =>
  (status != null && MESSAGE_STATUS[status]) || { label: status != null ? String(status) : "—", color: "gray" };

/**
 * A message's overall DELIVERY state, derived from its per-recipient counters — the message's own
 * `status` column is a record-lifecycle status (Active), not a delivery status, so it's not shown.
 */
export const messageDeliveryStatus = (m: {
  totalRecipients?: number | null;
  totalDelivered?: number | null;
  totalFailed?: number | null;
}): { label: string; color: string } => {
  const total = m.totalRecipients ?? 0;
  const delivered = m.totalDelivered ?? 0;
  const failed = m.totalFailed ?? 0;

  if (total === 0) {
    return { label: "Sent", color: "blue" };
  }
  if (delivered >= total) {
    return { label: "Delivered", color: "green" };
  }
  if (failed >= total) {
    return { label: "Failed", color: "red" };
  }
  if (delivered > 0 && failed > 0) {
    return { label: "Partly delivered", color: "orange" };
  }
  if (delivered > 0 || failed > 0) {
    return { label: "Sending", color: "blue" };
  }
  return { label: "Sent", color: "blue" };
};

export interface SentMessageChannel {
  channelKey?: string | null;
  name?: string | null;
}

export interface SentMessageRecipient {
  id?: string;
  recipient?: string | null;
  recipientType?: string | null;
  event?: string | null;
  status?: number | null;
  serviceProviderMessageId?: string | null;
  /** The actual number the text was sent from (Twilio picks it per message); null for non-SMS. */
  senderNumber?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface SentMessage {
  id?: string;
  contextPath?: string | null;
  purpose?: string | null;
  status?: number | null;
  subject?: string | null;
  fromName?: string | null;
  fromValue?: string | null;
  channelId?: string | null;
  channel?: SentMessageChannel | null;
  totalRecipients?: number | null;
  totalDelivered?: number | null;
  totalFailed?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  recipients?: SentMessageRecipient[] | null;
}

export interface SentMessagePreview {
  subject?: string | null;
  html?: string | null;
  text?: string | null;
}

export interface SentMessageFilter {
  contextPath?: string | null;
  contextPathPrefix?: string | null;
  purpose?: string[] | null;
  status?: number[] | null;
  channelKey?: string | null;
  /** #141 — delivered | pending | failed (several are OR-ed). */
  deliveryState?: SentMessageDeliveryState[] | null;
  /** #141 — email | sms | whatsapp, by the channel's key (several are OR-ed). */
  channelKind?: SentMessageChannelKind[] | null;
  /** #141 — what the row shows contains this (subject, sender name, a sender phone number; at most 200 characters). */
  searchText?: string | null;
  /** #141 — with searchText: the purposes whose LABEL (`MESSAGE_PURPOSE_LABELS`) contains it — `purposesLabelled`. */
  searchPurpose?: string[] | null;
}

export interface SentMessagePagination {
  page?: number;
  pageSize?: number;
}

export class SentMessageService {
  static async list(filter: SentMessageFilter, pagination?: SentMessagePagination): Promise<OpResult> {
    return ApiService.executeQuery(
      LIST_QUERY,
      {
        filter,
        // No page given is no cap — not a silent 50 (ROADMAP #118; every other service sends none either).
        params: { pagination: pagination ?? undefined, sortBy: [{ name: "createdAt", order: "DESC" }] },
      },
      { fetchPolicy: "network-only" },
    );
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(GET_QUERY, { id }, { fetchPolicy: "network-only" });
  }

  /** Re-render a sent message as it went out (subject + composed HTML/text) for on-screen preview. */
  static async preview(id: string): Promise<OpResult> {
    return ApiService.executeQuery(PREVIEW_QUERY, { id }, { fetchPolicy: "network-only" });
  }
}
